/**
 * Aviso na consola (`/`) após expiração da sessão da plataforma (#430).
 */
import { useEffect, useState } from 'react'
import {
  clearExpiredNotice,
  getExpiredNotice,
  onImpersonationChange,
  type ImpersonationExpiredNotice,
} from '../../lib/impersonation.js'
import { useI18n } from '../../lib/i18n.js'
import { intlLocaleFor } from '@self-restaurant/shared'
import type { StartTarget } from './StartImpersonationModal.js'

const RESTAURANT_MARK = '\uE000'

type Props = {
  restaurants: StartTarget[]
  onEnterAgain: (restaurant: StartTarget) => void
}

export function ExpiredSessionNotice({ restaurants, onEnterAgain }: Props) {
  const { t, lang } = useI18n()
  const [notice, setNotice] = useState<ImpersonationExpiredNotice | null>(() => getExpiredNotice())

  useEffect(() => onImpersonationChange(() => setNotice(getExpiredNotice())), [])

  if (!notice) return null

  const time = new Intl.DateTimeFormat(intlLocaleFor(lang), { timeStyle: 'short' }).format(
    new Date(notice.ended_at * 1000),
  )
  const phrase = t('admin.impersonation.expired_notice', {
    restaurant: RESTAURANT_MARK,
    time,
  })
  const markAt = phrase.indexOf(RESTAURANT_MARK)

  function dismiss() {
    clearExpiredNotice()
  }

  function enterAgain() {
    const target = restaurants.find((r) => r.slug === notice!.tenant_slug)
    clearExpiredNotice()
    if (target) onEnterAgain(target)
  }

  return (
    <div
      data-testid="impersonation-expired-notice"
      className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3"
      role="status"
    >
      <span className="text-sm text-amber-900 text-start flex-1 min-w-0">
        {markAt < 0 ? (
          phrase
        ) : (
          <>
            {phrase.slice(0, markAt)}
            <bdi>{notice.restaurant}</bdi>
            {phrase.slice(markAt + RESTAURANT_MARK.length)}
          </>
        )}
      </span>
      <button
        type="button"
        data-testid="impersonation-enter-again"
        className="text-sm font-semibold text-amber-900 underline hover:text-amber-700 min-h-11 px-2"
        onClick={enterAgain}
      >
        {t('admin.impersonation.enter_again')}
      </button>
      <button
        type="button"
        data-testid="impersonation-expired-dismiss"
        aria-label={t('admin.common.close')}
        className="text-amber-800 hover:text-amber-950 min-h-11 min-w-11"
        onClick={dismiss}
      >
        ×
      </button>
    </div>
  )
}
